import { useState } from 'react';
import { useTrainingStats } from '@/hooks/useTrainingStats';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import {
    Flame, Trophy, Target, TrendingUp, Clock, Zap,
    Award, ChevronRight, Calendar, BarChart3
} from 'lucide-react';
import { PageShell } from '@/components/ui/PageShell';

/* ─── Streak Hero ────────────────────────────────────────────── */
function StreakHero({ current, longest }: { current: number; longest: number }) {
    const flameSize = Math.min(current * 4 + 40, 80);
    return (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-600/20 via-red-600/10 to-bg-surface border border-orange-500/20 p-6">
            {/* Ambient glow */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/10 blur-[80px] rounded-full" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 blur-[60px] rounded-full" />

            <div className="relative flex items-center gap-6">
                {/* Flame */}
                <div className="flex-shrink-0 relative">
                    <div className="absolute inset-0 bg-orange-500/20 blur-xl rounded-full animate-pulse" />
                    <div className="relative flex items-center justify-center" style={{ width: flameSize, height: flameSize }}>
                        <Flame
                            size={flameSize * 0.7}
                            className={cn(
                                "transition-all duration-500",
                                current >= 7 ? "text-orange-400 drop-shadow-[0_0_15px_rgba(251,146,60,0.6)]"
                                    : current >= 3 ? "text-orange-500 drop-shadow-[0_0_10px_rgba(251,146,60,0.4)]"
                                        : current >= 1 ? "text-orange-600"
                                            : "text-text-muted/30"
                            )}
                        />
                    </div>
                </div>

                {/* Stats */}
                <div className="flex-1">
                    <div className="text-5xl font-black text-white leading-none">
                        {current}
                    </div>
                    <div className="text-sm font-bold text-orange-400/80 uppercase tracking-wider mt-1">
                        Day Streak
                    </div>
                    <div className="text-[11px] text-text-muted mt-2 flex items-center gap-1.5">
                        <Trophy size={12} className="text-gold-500" />
                        Best: <span className="text-white font-bold">{longest} days</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Quick Stat Card ────────────────────────────────────────── */
function QuickStat({ icon: Icon, label, value, sub, color }: {
    icon: any; label: string; value: string | number; sub?: string; color: string;
}) {
    return (
        <div className="p-4 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5 flex flex-col gap-2">
            <Icon size={18} className={color} />
            <div className="text-2xl font-black text-white leading-none">{value}</div>
            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">{label}</div>
            {sub && <div className="text-[10px] text-text-muted/60">{sub}</div>}
        </div>
    );
}

/* ─── Activity Heatmap (last 30 days) ────────────────────────── */
function ActivityHeatmap({ data }: { data: { date: string; minutes: number; sessions: number }[] }) {
    const maxMin = Math.max(...data.map(d => d.minutes), 1);

    return (
        <div className="p-5 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5">
            <div className="flex items-center gap-2 mb-4">
                <Calendar size={16} className="text-gold-500" />
                <h3 className="text-sm font-black text-white uppercase tracking-wide">Last 30 Days</h3>
            </div>

            <div className="grid grid-cols-10 gap-1.5">
                {data.map((d, i) => {
                    const intensity = d.minutes / maxMin;
                    const day = format(parseISO(d.date), 'EEE');
                    const dayNum = format(parseISO(d.date), 'd');
                    return (
                        <div key={i} className="group relative">
                            <div
                                className={cn(
                                    "aspect-square rounded-md transition-all duration-300 border",
                                    d.minutes > 0
                                        ? "border-gold-500/20 hover:scale-125 hover:z-10 cursor-pointer"
                                        : "border-white/[0.03] bg-white/[0.02]"
                                )}
                                style={d.minutes > 0 ? {
                                    backgroundColor: `rgba(212,175,55,${0.1 + intensity * 0.5})`,
                                    boxShadow: intensity > 0.5 ? `0 0 ${intensity * 10}px rgba(212,175,55,${intensity * 0.3})` : 'none',
                                } : undefined}
                            />
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 rounded-lg bg-bg-elevated border border-white/10 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20 shadow-xl">
                                <div className="font-bold">{day} {dayNum}</div>
                                <div className="text-text-muted">{d.minutes}m · {d.sessions} session{d.sessions !== 1 ? 's' : ''}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-end gap-1.5 mt-3 text-[9px] text-text-muted">
                <span>Less</span>
                {[0.05, 0.15, 0.3, 0.5, 0.7].map((o, i) => (
                    <div key={i} className="w-3 h-3 rounded-sm" style={{ backgroundColor: `rgba(212,175,55,${o})` }} />
                ))}
                <span>More</span>
            </div>
        </div>
    );
}

/* ─── Position Donut ─────────────────────────────────────────── */
function PositionDonut({ breakdown }: { breakdown: { neutral: number; top: number; bottom: number } }) {
    const total = breakdown.neutral + breakdown.top + breakdown.bottom;
    if (total === 0) {
        return (
            <div className="p-5 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5 text-center py-10">
                <Target size={32} className="mx-auto text-text-muted/20 mb-3" />
                <p className="text-text-muted text-sm">No position data yet</p>
            </div>
        );
    }

    const segments = [
        { label: 'Neutral', value: breakdown.neutral, color: '#3B82F6', pct: Math.round((breakdown.neutral / total) * 100) },
        { label: 'Top', value: breakdown.top, color: '#10B981', pct: Math.round((breakdown.top / total) * 100) },
        { label: 'Bottom', value: breakdown.bottom, color: '#F59E0B', pct: Math.round((breakdown.bottom / total) * 100) },
    ];

    const size = 120;
    const strokeWidth = 16;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    let accumOffset = 0;

    return (
        <div className="p-5 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5">
            <div className="flex items-center gap-2 mb-4">
                <Target size={16} className="text-blue-400" />
                <h3 className="text-sm font-black text-white uppercase tracking-wide">Position Split</h3>
            </div>

            <div className="flex items-center gap-6">
                <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
                        {segments.map((seg, i) => {
                            const dashLength = (seg.pct / 100) * circumference;
                            const dashGap = circumference - dashLength;
                            const offset = accumOffset;
                            accumOffset += dashLength;

                            return (
                                <circle
                                    key={i}
                                    cx={size / 2} cy={size / 2} r={radius}
                                    fill="transparent" stroke={seg.color}
                                    strokeWidth={strokeWidth} strokeLinecap="round"
                                    strokeDasharray={`${dashLength} ${dashGap}`}
                                    strokeDashoffset={-offset}
                                    className="transition-all duration-1000"
                                    style={{ filter: `drop-shadow(0 0 4px ${seg.color}40)` }}
                                />
                            );
                        })}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-black text-white">{total}m</span>
                    </div>
                </div>

                <div className="flex-1 space-y-3">
                    {segments.map((seg, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                            <div className="flex-1">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-white">{seg.label}</span>
                                    <span className="text-xs text-text-muted">{seg.pct}%</span>
                                </div>
                                <div className="h-1 bg-white/5 rounded-full mt-1 overflow-hidden">
                                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${seg.pct}%`, backgroundColor: seg.color }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ─── Weekly Trend Bars ──────────────────────────────────────── */
function WeeklyTrend({ data }: { data: { week: string; minutes: number; sessions: number }[] }) {
    const maxMin = Math.max(...data.map(d => d.minutes), 1);

    return (
        <div className="p-5 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5">
            <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={16} className="text-emerald-400" />
                <h3 className="text-sm font-black text-white uppercase tracking-wide">Weekly Volume</h3>
            </div>

            <div className="flex items-end gap-2 h-32">
                {data.map((d, i) => {
                    const height = d.minutes > 0 ? Math.max((d.minutes / maxMin) * 100, 8) : 4;
                    const isLast = i === data.length - 1;
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group">
                            <div className="text-[9px] text-text-muted opacity-0 group-hover:opacity-100 transition-opacity font-bold">
                                {d.minutes}m
                            </div>
                            <div className="w-full relative">
                                <div
                                    className={cn(
                                        "w-full rounded-t-lg transition-all duration-500",
                                        isLast ? "bg-gradient-to-t from-gold-600 to-gold-400" : "bg-white/10 group-hover:bg-white/20"
                                    )}
                                    style={{
                                        height: `${height}px`,
                                        boxShadow: isLast ? '0 0 15px rgba(212,175,55,0.3)' : 'none',
                                    }}
                                />
                            </div>
                            <span className={cn(
                                "text-[9px] font-bold",
                                isLast ? "text-gold-400" : "text-text-muted/50"
                            )}>
                                {d.week.split(' ')[0]}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── Achievements Grid ──────────────────────────────────────── */
function AchievementsSection({ unlocked, locked }: {
    unlocked: { id: string; name: string; description: string; icon: string }[];
    locked: { id: string; name: string; description: string; icon: string }[];
}) {
    const [showAll, setShowAll] = useState(false);
    const displayLocked = showAll ? locked : locked.slice(0, 4);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <Award size={18} className="text-gold-500" />
                <h2 className="text-lg font-black text-white uppercase tracking-wide">Achievements</h2>
                <span className="ml-auto text-xs text-text-muted font-bold">{unlocked.length}/{unlocked.length + locked.length}</span>
            </div>

            {/* Unlocked */}
            {unlocked.length > 0 && (
                <div className="grid grid-cols-2 gap-2.5">
                    {unlocked.map(a => (
                        <div key={a.id} className="p-3.5 rounded-2xl bg-gradient-to-br from-gold-500/10 to-transparent border border-gold-500/20 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-16 h-16 bg-gold-500/5 blur-2xl rounded-full" />
                            <div className="relative">
                                <div className="text-2xl mb-2">{a.icon}</div>
                                <h4 className="text-xs font-black text-gold-300 uppercase tracking-wide">{a.name}</h4>
                                <p className="text-[10px] text-text-muted mt-0.5 leading-snug">{a.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Locked */}
            {displayLocked.length > 0 && (
                <>
                    <div className="grid grid-cols-2 gap-2.5">
                        {displayLocked.map(a => (
                            <div key={a.id} className="p-3.5 rounded-2xl bg-bg-surface/30 border border-white/[0.04] opacity-40">
                                <div className="text-2xl mb-2 grayscale">{a.icon}</div>
                                <h4 className="text-xs font-bold text-text-muted uppercase tracking-wide">{a.name}</h4>
                                <p className="text-[10px] text-text-muted/50 mt-0.5 leading-snug">{a.description}</p>
                            </div>
                        ))}
                    </div>
                    {locked.length > 4 && (
                        <button
                            onClick={() => setShowAll(!showAll)}
                            className="w-full text-center text-[11px] font-bold text-text-muted uppercase tracking-wider py-2 hover:text-white transition-colors flex items-center justify-center gap-1"
                        >
                            {showAll ? 'Show Less' : `Show ${locked.length - 4} More`}
                            <ChevronRight size={14} className={cn("transition-transform", showAll && "rotate-90")} />
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

/* ─── Personal Records ───────────────────────────────────────── */
function PersonalRecords({ records }: { records: { longestSession: number; mostRoundsInSession: number; bestWeek: number } }) {
    const formatTime = (sec: number) => {
        if (sec < 60) return `${sec}s`;
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return s > 0 ? `${m}m ${s}s` : `${m}m`;
    };

    const prs = [
        { label: 'Longest Session', value: formatTime(records.longestSession), icon: Clock, color: 'text-blue-400' },
        { label: 'Most Rounds', value: records.mostRoundsInSession.toString(), icon: Zap, color: 'text-purple-400' },
        { label: 'Best Week', value: `${records.bestWeek}m`, icon: TrendingUp, color: 'text-emerald-400' },
    ];

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Trophy size={18} className="text-gold-500" />
                <h2 className="text-lg font-black text-white uppercase tracking-wide">Personal Records</h2>
            </div>
            <div className="grid grid-cols-3 gap-2.5">
                {prs.map((pr, i) => (
                    <div key={i} className="p-3.5 rounded-2xl bg-bg-surface/50 backdrop-blur-sm border border-white/5 text-center">
                        <pr.icon size={18} className={cn(pr.color, "mx-auto mb-2")} />
                        <div className="text-lg font-black text-white leading-none">{pr.value}</div>
                        <div className="text-[9px] font-bold text-text-muted uppercase tracking-wider mt-1.5">{pr.label}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ═══ MAIN PAGE ════════════════════════════════════════════════ */
export function AnalyticsPage() {
    const stats = useTrainingStats();

    return (
        <PageShell
            title="Your Stats"
            subtitle="Analytics dashboard"
            right={
                <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20">
                    <BarChart3 className="w-5 h-5 text-orange-400" />
                </div>
            }
        >
            <div className="px-5 space-y-6 pt-2">
                {/* 1. Streak Hero */}
                <StreakHero current={stats.currentStreak} longest={stats.longestStreak} />

                {/* 2. Quick Stats Grid */}
                <div className="grid grid-cols-2 gap-2.5">
                    <QuickStat icon={Target} label="Total Sessions" value={stats.totalSessions} color="text-blue-400" />
                    <QuickStat icon={Clock} label="Total Minutes" value={stats.totalMinutes} color="text-emerald-400" />
                    <QuickStat icon={Zap} label="This Week" value={`${stats.weeklyMinutes}m`} sub={`${stats.weeklySessions} sessions`} color="text-gold-400" />
                    <QuickStat icon={TrendingUp} label="This Month" value={`${stats.monthlyMinutes}m`} sub={`${stats.monthlySessions} sessions`} color="text-purple-400" />
                </div>

                {/* 3. Activity Heatmap */}
                <ActivityHeatmap data={stats.dailyActivity} />

                {/* 4. Weekly Trend */}
                <WeeklyTrend data={stats.weeklyTrend} />

                {/* 5. Position Breakdown */}
                <PositionDonut breakdown={stats.positionBreakdown} />

                {/* 6. Personal Records */}
                <PersonalRecords records={stats.personalRecords} />

                {/* 7. Achievements */}
                <AchievementsSection
                    unlocked={stats.unlockedAchievements}
                    locked={stats.lockedAchievements}
                />
            </div>
        </PageShell>
    );
}
